const PARIS_FMT = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function todayInParis(): string {
  return PARIS_FMT.format(new Date());
}

const PARIS_TIME_FMT = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
});

// HH:MM in Paris, from an ISO timestamp.
export function formatTimeParis(iso: string): string {
  return PARIS_TIME_FMT.format(new Date(iso));
}

const PARIS_DATETIME_FMT = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  dateStyle: 'full',
  timeStyle: 'short',
});

// "jeudi 3 septembre 2026 à 08:00" — the unambiguous form, for a message's header.
export function formatDateTimeParis(iso: string): string {
  return PARIS_DATETIME_FMT.format(new Date(iso));
}

/**
 * Relative time for a full timestamp (a message's createdAt), Paris calendar:
 * "à l'instant", "il y a 12 min", "14:05" today, "hier 08:00", then dd/mm/yy.
 */
export function formatRelativeParis(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const minutes = Math.floor((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const day = PARIS_FMT.format(then);
  const today = PARIS_FMT.format(now);
  if (day === today) return formatTimeParis(iso);
  if (day === addDays(today, -1)) return `hier ${formatTimeParis(iso)}`;
  return formatFR(day);
}

export function formatShort(iso: string | null): string {
  if (!iso) return '';
  const today = todayInParis();
  if (iso === today) return 'Today';
  const daysAhead = diffDays(today, iso);
  if (daysAhead > 0 && daysAhead <= 6) {
    return dayOfWeekShort(iso);
  }
  return formatFR(iso);
}

// dd/mm/yy — French short format (no weekday).
export function formatFR(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const yy = String(y).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function diffDays(fromIso: string, toIso: string): number {
  const parse = (s: string): number => {
    const [y, m, d] = s.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((parse(toIso) - parse(fromIso)) / 86400000);
}

export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function dayOfWeekShort(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
  });
}

export function dayOfMonth(iso: string): number {
  const [, , d] = iso.split('-').map(Number) as [number, number, number];
  return d;
}

// Returns YYYY-MM-DD for the Monday that starts the week containing `iso`.
export function mondayOfWeek(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = dt.getUTCDay(); // 0 = Sunday, 1 = Monday…
  const offset = day === 0 ? -6 : 1 - day;
  dt.setUTCDate(dt.getUTCDate() + offset);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// ISO 8601 week number of the given date (Mon-Sun week, week 1 contains Jan 4).
export function isoWeekNumber(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  // Thursday of the current week is in the year that owns the week.
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  return Math.ceil(((dt.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// « Thursday 1 October » / « jeudi 1 octobre » — le sur-titre d'un en-tête de page (`.entete`).
export function formatLong(iso: string, langue: 'en' | 'fr' = 'en'): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(langue === 'fr' ? 'fr-FR' : 'en-GB', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

// « 28 Sep – 4 Oct » : une semaine, du lundi au dimanche.
export function formatWeekRange(monday: string): string {
  const fmt = (iso: string): string => {
    const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'short',
    });
  };
  return `${fmt(monday)} – ${fmt(addDays(monday, 6))}`;
}
